import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import LayoutPages from '../../components/LayoutPages/LayoutPages';
import DataState from '../../components/ui/DataState';
import useDataStatus from '../../components/ui/useDataStatus';
import { useAuthContext } from '../../context/authContext';

moment.locale('es');

/* Paleta clara: esta pantalla se mira de cerca, en una laptop y un sábado por
   la mañana. Nada que ver con la comanda, que vive proyectada en cocina. */
const C = {
  fondo: '#F6F8F6',
  tarjeta: '#FFFFFF',
  linea: '#E4EAE6',
  tinta: '#16211B',
  suave: '#6B7A72',
  verde: '#177A4C',
  verdeSuave: '#EAF6F0',
  ambar: '#9A6B12',
  ambarSuave: '#FDF3DF',
};

/* El mismo color por opcion que en el calendario del mes: la opcion 2 es azul
   en las dos pantallas, asi no hay que volver a aprender nada al cambiar. */
const OPCION = [
  { tinta: '#177A4C', fondo: '#E7F5EE' },
  { tinta: '#1C6B9C', fondo: '#E6F1F8' },
  { tinta: '#9A5B14', fondo: '#FAF0DF' },
];

const DIA = (f) => {
  const t = moment(f).format('dddd D');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** 1.076 en vez de 1076: en un número de compra, el punto se agradece. */
const miles = (n) => (n == null ? '—' : Number(n).toLocaleString('es-PE'));

const Dato = ({ titulo, valor, nota, acento }) => (
  <Box sx={{
    flex: 1, minWidth: 190, px: 2.5, py: 2,
    bgcolor: acento ? C.verdeSuave : C.tarjeta,
    border: `1px solid ${acento ? 'transparent' : C.linea}`, borderRadius: 3,
  }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em',
      textTransform: 'uppercase', color: C.suave }}>{titulo}</Typography>
    <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, color: acento ? C.verde : C.tinta,
      fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
    {nota && <Typography sx={{ fontSize: 13, color: C.suave, lineHeight: 1.35, mt: .3 }}>{nota}</Typography>}
  </Box>
);

const SemanaPage = () => {
  const { token, baseUrl } = useAuthContext();
  const { status, start, done, fail } = useDataStatus();
  const [data, setData] = useState(null);

  /* Arranca en la semana que se va a comprar: el sábado y el domingo ya se
     está mirando la que viene, no la que termina. */
  const [desde, setDesde] = useState(() => {
    const hoy = moment();
    const base = hoy.isoWeekday() >= 6 ? hoy.clone().add(1, 'week') : hoy;
    return base.startOf('isoWeek').format('YYYY-MM-DD');
  });

  const traer = useCallback(() => {
    start();
    axios.get(`${baseUrl}admin/semana?desde=${desde}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data?.data ?? r.data); done(); })
      .catch(() => fail());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, token, baseUrl]);

  useEffect(() => { traer(); }, [traer]);

  const previa = data?.semanaAnterior;
  /* Regla de tres sobre la semana pasada, que es lo que el coordinador ya hace
     de cabeza. No es un pronóstico: es su cuenta, escrita. */
  const porCliente = previa?.clientes ? previa.porciones / previa.clientes : null;
  const estimado = porCliente && data?.clientes ? Math.round(data.clientes * porCliente) : null;
  const estimadoDia = estimado ? Math.round(estimado / 5) : null;

  const elegidas = (data?.dias || []).reduce((s, d) => s + (d.porciones || 0), 0);
  const avance = estimado ? Math.round((elegidas / estimado) * 100) : null;

  /* Una semana que ya paso no se estima: se cuenta. Con los mismos tres
     recuadros decia "1.076 ya elegidas, 107%" sobre un estimado que ya no
     significaba nada, y el conteo de clientes sale sesgado hacia abajo porque
     a estas alturas muchos ya gastaron sus envios. */
  const semanaCerrada = (data?.dias || []).length > 0 && data.dias.every((d) => d.cerrado);

  /* Un plato que sale dos veces en la misma semana se compra una vez. */
  const repetidosEnLaSemana = (() => {
    const cuenta = new Map();
    (data?.dias || []).forEach((d) => (d.platos || []).forEach((p) => {
      cuenta.set(p.menuId, (cuenta.get(p.menuId) || 0) + 1);
    }));
    return new Set([...cuenta.entries()].filter(([, n]) => n > 1).map(([id]) => id));
  })();

  const mover = (semanas) =>
    setDesde(moment(desde).add(semanas, 'week').format('YYYY-MM-DD'));

  return (
    <LayoutPages>
      <Box sx={{ bgcolor: C.fondo, m: -3, p: 3, minHeight: '100vh' }}>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: C.tinta, letterSpacing: '-.4px' }}>
              Semana de cocina
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: C.suave }}>
              Lo que se cocina y cuánto, para la compra del sábado.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: C.tarjeta, border: `1px solid ${C.linea}`, borderRadius: 999, px: 1, py: .5 }}>
            <IconButton size="small" onClick={() => mover(-1)}><ChevronLeftRoundedIcon /></IconButton>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.tinta, minWidth: 150, textAlign: 'center' }}>
              {data ? `${moment(data.desde).format('D [de] MMMM')} – ${moment(data.hasta).format('D')}` : '—'}
            </Typography>
            <IconButton size="small" onClick={() => mover(1)}><ChevronRightRoundedIcon /></IconButton>
          </Box>
        </Box>

        <DataState status={status} isEmpty={!data} onRetry={traer} variant="list" skeletonCount={2}
          emptyTitle="No hay menú para esta semana"
          emptyDescription="Programa los días desde «Programar» y vuelve aquí.">

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
            {semanaCerrada ? (
              <>
                <Dato acento titulo="Porciones de la semana" valor={miles(elegidas)}
                  nota="definitivo · es lo que se cocinó" />
                <Dato titulo="Promedio por día" valor={miles(Math.round(elegidas / 5))}
                  nota="sobre los cinco días de reparto" />
              </>
            ) : (
              <>
                <Dato titulo="Clientes" valor={miles(data?.clientes)}
                  nota="con plan activo y envíos por usar" />
                <Dato acento titulo="Porciones de la semana" valor={miles(estimado)}
                  nota={previa ? `≈ ${estimadoDia} por día · la semana pasada fueron ${miles(previa.porciones)} con ${previa.clientes} clientes` : null} />
                <Dato titulo="Ya elegidas" valor={miles(elegidas)}
                  nota={avance != null ? `${avance}% · el resto se cierra el domingo 10 p.m.` : null} />
              </>
            )}
          </Box>

          <Box sx={{ display: 'grid', gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' } }}>
            {(data?.dias || []).map((dia) => (
              <Box key={dia.fecha} sx={{
                bgcolor: C.tarjeta, border: `1px solid ${C.linea}`, borderRadius: 3,
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}>
                {/* El numero del dia va aca y una sola vez. Repetir el estimado
                    en cada plato llenaba la tarjeta de tres cifras iguales que
                    no decian nada: lo que se compra es el total del dia. */}
                <Box sx={{ px: 2.2, py: 1.6, borderBottom: `1px solid ${C.linea}`,
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: C.tinta, lineHeight: 1.2 }}>
                      {DIA(dia.fecha)}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: dia.cerrado ? C.verde : C.suave }}>
                      {dia.cerrado ? 'cerrado · definitivo' : 'estimado'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1,
                    color: dia.cerrado ? C.tinta : C.verde, fontVariantNumeric: 'tabular-nums' }}>
                    {dia.cerrado ? miles(dia.porciones) : miles(estimadoDia)}
                  </Typography>
                </Box>

                <Box sx={{ p: 2.2, display: 'grid', gap: 1.8, flex: 1 }}>
                  {(dia.platos || []).length === 0 && (
                    <Typography sx={{ fontSize: 14, color: C.suave }}>Sin platos programados.</Typography>
                  )}

                  {(dia.platos || []).map((p) => {
                    /* Lo que se lleva cada opcion de lo que YA se eligio. Es el
                       unico dato por plato que se mueve de verdad, y es la
                       senal temprana de cual se va a llevar mas olla. */
                    const parte = dia.porciones ? Math.round((p.porciones / dia.porciones) * 100) : 0;
                    return (
                      <Box key={p.menuId} sx={{ display: 'flex', gap: 1.4, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 24, height: 24, mt: .2, borderRadius: 1.2, flexShrink: 0,
                          bgcolor: (OPCION[p.op - 1] || OPCION[0]).fondo,
                          color: (OPCION[p.op - 1] || OPCION[0]).tinta,
                          display: 'grid', placeItems: 'center',
                          fontSize: 13, fontWeight: 800 }}>{p.op}</Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: C.tinta, lineHeight: 1.3 }}>
                            {p.nombre}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: .8, flexWrap: 'wrap', mt: .5 }}>
                            {repetidosEnLaSemana.has(p.menuId) && (
                              <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700,
                                px: .9, py: .2, borderRadius: 999, bgcolor: C.ambarSuave, color: C.ambar }}>
                                repite esta semana
                              </Typography>
                            )}
                            {p.ultimaVez && (
                              <Typography component="span" sx={{ fontSize: 11.5, color: C.suave }}>
                                salió el {moment(p.ultimaVez).format('D MMM')}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 54 }}>
                          <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1,
                            color: C.tinta, fontVariantNumeric: 'tabular-nums' }}>
                            {miles(p.porciones)}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.suave }}>{parte}%</Typography>
                          {/* La barra deja ver de un golpe si una opcion se esta
                              llevando el doble que otra, que es lo que pasa. */}
                          <Box sx={{ mt: .5, height: 3, borderRadius: 2, bgcolor: C.linea, overflow: 'hidden' }}>
                            <Box sx={{ width: `${parte}%`, height: '100%',
                              bgcolor: (OPCION[p.op - 1] || OPCION[0]).tinta }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {(dia.extras || []).length > 0 && (
                  <Box sx={{ px: 2.2, py: 1.4, borderTop: `1px solid ${C.linea}`, bgcolor: '#FBFCFB' }}>
                    {dia.extras.map((e) => (
                      <Box key={`${e.tipo}-${e.nombre}`} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
                          textTransform: 'uppercase', minWidth: 62,
                          color: { starter: '#6B4FBB', drinks: '#0E7C86', snacks: '#A8336B' }[e.tipo] || C.suave }}>
                          {{ starter: 'Entrada', drinks: 'Bebida', snacks: 'Snack', breakfast: 'Desayuno' }[e.tipo] || e.tipo}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: C.tinta, lineHeight: 1.4 }}>{e.nombre}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 12.5, color: C.suave, mt: 2.5, maxWidth: 820, lineHeight: 1.5 }}>
            El número grande de cada día es el estimado mientras el día sigue abierto, y pasa a ser
            lo que de verdad se pidió cuando cierra. Los números de cada plato son elecciones ya
            hechas: por semana las tres opciones se emparejan cerca del tercio, pero un día una
            puede llevarse el doble que otra, así que sirven para ajustar, no para comprar.
          </Typography>

        </DataState>
      </Box>
    </LayoutPages>
  );
};

export default SemanaPage;
